import { useEffect, useState } from "react";
import { api } from "@/client/lib/api";
import { Button } from "@/client/components/ui/button";
import { Input } from "@/client/components/ui/input";
import { useAdminSaveToast } from "@/client/components/admin/AdminSaveToast";
import { SparklesIcon, AlertTriangleIcon } from "lucide-react";

// Mirror of the server contract (app-settings.ts). Keep in sync with the Zod bounds on
// PUT /api/admin/dashboard/settings/keuze-threshold.
const THRESHOLD_MIN = 0;
const THRESHOLD_MAX = 5;
const THRESHOLD_DEFAULT = 1;

// The keuze auto-select threshold lives under the admin dashboard route group; reach the kebab-case
// segment via the index accessor, matching PlanningDeadlineControl.
const thresholdResource = () => (api.api.admin.dashboard as any).settings["keuze-threshold"];

// Compact KUVO-wide setting: when are passende keuzeprogramma's pre-ticked for a group? Styled like
// PlanningDeadlineControl so the two settings read as one neat panel on the Instellingen page.
export function KeuzeThresholdControl() {
  const [threshold, setThreshold] = useState<number>(THRESHOLD_DEFAULT);
  const [draft, setDraft] = useState<string>(String(THRESHOLD_DEFAULT));
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savedToast = useAdminSaveToast();

  useEffect(() => {
    (async () => {
      try {
        const res = await thresholdResource().$get();
        if (res.ok) {
          const data = await res.json();
          const value = typeof data.threshold === "number" ? data.threshold : THRESHOLD_DEFAULT;
          setThreshold(value);
          setDraft(String(value));
        }
      } catch {
        /* falls back to the default; saving still works */
      }
      setLoaded(true);
    })();
  }, []);

  const parsed = draft.trim() === "" ? null : Number(draft);
  const valid =
    parsed !== null && Number.isInteger(parsed) && parsed >= THRESHOLD_MIN && parsed <= THRESHOLD_MAX;
  const dirty = parsed !== threshold;
  const showHighWarning = valid && parsed !== null && parsed > 1;

  async function handleSave() {
    if (!valid || parsed === null) return;
    setSaving(true);
    setError(null);
    try {
      const res = await thresholdResource().$put({ json: { threshold: parsed } });
      if (res.ok) {
        const data = await res.json();
        const value = typeof data.threshold === "number" ? data.threshold : parsed;
        setThreshold(value);
        setDraft(String(value));
        savedToast.show();
      } else {
        setError("Opslaan mislukt. Controleer de waarde.");
      }
    } catch {
      setError("Er ging iets mis bij het opslaan.");
    } finally {
      setSaving(false);
    }
  }

  if (!loaded) return null;

  return (
    <div className="rounded-lg border bg-muted/20 p-3">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
        <SparklesIcon size={15} /> Keuzeprogramma's automatisch aanvinken
      </h3>
      <p className="mt-1 max-w-prose text-xs text-muted-foreground">
        0 = nooit aanvinken, 1 = alleen bij precies een passende optie, hoger = aanvinken zodra er maximaal zoveel zijn.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Input
          id="keuze-threshold"
          type="number"
          inputMode="numeric"
          min={THRESHOLD_MIN}
          max={THRESHOLD_MAX}
          step={1}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(null);
          }}
          aria-invalid={!valid}
          className="w-20"
        />
        <Button size="sm" onClick={handleSave} disabled={saving || !valid || !dirty}>
          {saving ? "Opslaan..." : "Opslaan"}
        </Button>
        <span className="text-xs text-muted-foreground">passende optie(s)</span>
      </div>
      {!valid && (
        <p className="mt-2 text-xs text-destructive">
          Kies een heel getal tussen {THRESHOLD_MIN} en {THRESHOLD_MAX}.
        </p>
      )}
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
      {showHighWarning && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700">
          <AlertTriangleIcon size={14} className="mt-px shrink-0" />
          Scholen ervaren dit dan mogelijk minder als een keuze.
        </p>
      )}
      {savedToast.toast}
    </div>
  );
}
